/**
 * LabRuntime — orquestrador do runtime.
 *
 * Carrega o manifest via /api/modules/module_lab/sessions/{id} → game_slug →
 * /api/modules/module_lab/games/{slug}. Escolhe master ou player view baseado
 * no role da Session (URL param por enquanto; fase 3 já autoriza via WS).
 */

import { useEffect, useState } from 'react'

import '../styles/tokens.css'
import '../styles/helpers.css'
import { AdapterProvider, useAdapter } from '../adapter'
import { SessionProvider, useSession } from '../session'
import { useManifest } from '../hooks/useManifest'
import { useSessionState } from '../hooks/useSessionState'
import type { Manifest } from '../types/manifest'
import type { Session } from '../types/session'
import { GameMasterPanel } from './GameMasterPanel'
import { GamePlayerView } from './GamePlayerView'
import { RoleGate } from './RoleGate'

interface LabRuntimeProps {
  /** Quando passado, monta sem URL params (modo embed na GameSession). */
  embedSession?: Session
}

export default function LabRuntime({ embedSession }: LabRuntimeProps = {}) {
  return (
    <SessionProvider embedSession={embedSession}>
      <SessionAwareAdapter />
    </SessionProvider>
  )
}

function SessionAwareAdapter() {
  const session = useSession()
  return (
    <AdapterProvider session={session}>
      <GameLoader />
    </AdapterProvider>
  )
}

function GameLoader() {
  const session = useSession()
  const adapter = useAdapter()
  const [gameSlug, setGameSlug] = useState<string | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)

  // O gameSlug pode vir do sessionSnapshot do WS; em modo mock, fetch REST.
  useEffect(() => {
    let cancelled = false

    const offSnap = adapter.on('sessionSnapshot', (snap: { gameSlug?: string }) => {
      if (!cancelled && snap?.gameSlug) setGameSlug(snap.gameSlug)
    })

    fetch(`/api/modules/module_lab/sessions/${encodeURIComponent(session.sessionId)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data: { game_slug?: string }) => {
        if (cancelled) return
        if (data.game_slug) setGameSlug(data.game_slug)
      })
      .catch((err: unknown) => {
        if (!cancelled) setLookupError(err instanceof Error ? err.message : String(err))
      })

    return () => {
      cancelled = true
      offSnap()
    }
  }, [adapter, session.sessionId])

  const { manifest, loading, error } = useManifest(gameSlug)

  if (lookupError) {
    return <StatusScreen>Erro ao resolver sessão: {lookupError}</StatusScreen>
  }
  if (error) {
    return <StatusScreen>Erro ao carregar jogo: {error}</StatusScreen>
  }
  if (!gameSlug || loading || !manifest) {
    return <StatusScreen>Carregando jogo…</StatusScreen>
  }

  return (
    <RoleGate>
      <RoleView manifest={manifest} />
    </RoleGate>
  )
}

function RoleView({ manifest }: { manifest: Manifest }) {
  // Usa actualRole do backend (vem no sessionSnapshot) — fonte de verdade.
  // Em mock sem snapshot, o useSessionState inicia com null, mas o mock
  // emite connected sincronamente... ainda assim, fallback pro session.role.
  const session = useSession()
  const { actualRole } = useSessionState()
  const effectiveRole = actualRole ?? session.role
  return effectiveRole === 'master' ? (
    <GameMasterPanel manifest={manifest} />
  ) : (
    <GamePlayerView manifest={manifest} />
  )
}

function StatusScreen({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--color-lab-bg)',
        color: 'var(--color-lab-ink-3)',
        fontFamily: 'var(--font-lab-sans)',
      }}
    >
      <div className="animate-pulse">{children}</div>
    </div>
  )
}
